import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Button, Input, Typography, Select, Space } from 'antd'
import _ from 'lodash'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import * as Rules from 'admin/lib/ValidationRules'
import { usDateToFormDate } from 'admin/lib/dates'

import {
  Section,
  AutoCompleteFormItem,
  FormItem,
  FormSpace,
  FormSpaceSmall,
  DateTimeUS,
  FormRowWithValidation
} from 'admin/lib/Forms'
import { Undo } from 'admin/lib/Icons/Undo'

import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import { usDateFormatter } from 'admin/lib/Functions/timeZoneHelper'
import {
  ConvertToMetersObject,
  Delivery as fields,
  DeliveryImperial as fieldsImperial,
  DeliveryProductCertOptions,
  IMPERIAL_VOLUME_UNITS,
  IMPERIAL_WEIGHT_UNITS,
  METRIC_VOLUME_UNITS,
  METRIC_WEIGHT_UNITS,
  MeasurementUoM
} from 'admin/lib/Records'
import {
  CheckHeightLengthWidth,
  CheckProductName,
  CheckQuantity,
  CheckVolume,
  CheckWeight
} from 'admin/lib/SimpleDataValidation'
import {
  AddMultipleLineItemsModal,
  UnitSelector,
  useLineItemShortcuts
} from 'admin/pages/RecordEdit/common'

import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'
import { LineItemTitle } from 'admin/pages/RecordEdit_v2/common/styledComponents'

import { returnValueForLineItemField } from 'admin/pages/RecordEdit/common/returnValueForLineItemField'

import { ReferenceProductItemSelect } from './ReferenceProductItemSelect'
import { calculateVolume, calculateImperialVolume } from './calculateVolume'
import { estimateRebarWeight } from './estimateRebarWeight'

export function DeliveryLineItem({
  index,
  item,
  onDuplicate,
  onDuplicateMultiple,
  onRemove,
  isImperial
}) {
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [productDetailsOpen, setProductDetailsOpen] = useState(true)
  const [measurementsOpen, setMeasurementsOpen] = useState(true)
  const [concreteDatesOpen, setConcreteDatesOpen] = useState(
    !!item.productionTime
  )
  const [isDuplicateItemsMultipleOpen, setIsDuplicateItemsMultipleOpen] =
    useState(false)

  const [producedAtDateWarningMessage, setProducedAtDateWarningMessage] =
    useState('')
  const [showProducedAtDateWarning, setShowProducedAtDateWarning] =
    useState(false)

  const [usedAtDateWarningMessage, setUsedAtDateWarningMessage] = useState('')
  const [showUsedAtDateWarning, setShowUsedAtDateWarning] = useState(false)

  const lineItem = useLineItemStore(state => state[index])
  const deleteLineItem = useLineItemStore(state => state.deleteKey)
  const duplicateLineItem = useLineItemStore(state => state.duplicateKey)
  const { moduleName, recordId } = useParams()

  const namePrefix = `uniquePrefix[${index}].`
  // Alias Used for autocomplete fields
  const aliasPrefix = `data.items[${index}].`

  const REBAR_LENGTH_WARNING_MESSAGE =
    'The value entered for Length seems big, have you set the correct unit?'

  const REBAR_WIDTH_WARNING_MESSAGE =
    'The value entered for Width seems big, have you set the correct unit?'

  const PRODUCED_AT_DATE_AFTER_ARRIVAL_DATE_WARNING_MESSAGE =
    'Entered Produced At date is after the Arrival Date for this record'

  const PRODUCED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE =
    'Entered Produced At date is after the Exit Date for this record'

  const USED_AT_DATE_BEFORE_PRODUCED_AT_DATE_WARNING_MESSAGE =
    'Entered Used At date is before the Produced At Date for this record'

  const USED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE =
    'Entered Used At date is after the Exit Date for this record'

  const form = useForm({
    reValidateMode: 'onChange',
    mode: 'all'
  })

  const { getValues } = useFormContext()
  const deliveryArrivalDate = getValues().data?.deliveryArrivalDate
  const deliveryExitDate = getValues().data?.deliveryExitDate

  const [name, setName] = useState(item.name)

  const { TextArea } = Input

  const dataChange = event => {
    setUnsavedChanges(true)

    // Handle multiple events (for select fields that update other fields)
    if (Array.isArray(event)) {
      const newLineItem = { ...lineItem }
      event.forEach(eventPart => {
        const lineItemKey = eventPart.target.name.split(namePrefix).pop()
        newLineItem[lineItemKey] = eventPart.target.value
      })

      useLineItemStore.setState({ [index]: newLineItem })
    } else {
      const lineItemKey = event.target.name.split(namePrefix).pop()

      if (lineItemKey === 'name') {
        setName(event.target.value)
      }

      const newLineItem = {
        ...lineItem,
        [lineItemKey]:
          event.target.type === 'number'
            ? parseFloat(event.target.value)
            : event.target.value
      }
      useLineItemStore.setState({ [index]: newLineItem })

      // If UoM change also trigger a blur event for the parent input
      // so that we trigger any validation rules
      if (
        lineItemKey.includes('UoM') &&
        lineItem[lineItemKey] !== event.target.value
      ) {
        const selector = `[name='${event.target.name.split('UoM')[0]}']`
        setTimeout(() => {
          document.querySelector(selector)?.blur()
          document.querySelector(selector)?.focus()
        }, 1)
      }
    }
  }

  const handleFocus = event => {
    const clipboardValue = sessionStorage.getItem('clipboard')

    if (clipboardValue && clipboardValue !== '') {
      const updatedValue = returnValueForLineItemField(
        clipboardValue,
        event.target.type,
        event.target.value
      )

      if (event.target.name.startsWith('uniquePrefix')) {
        // All 'real' fields starts with 'uniquePrefix'
        // so we'll only handle those first

        form.setValue(event.target.name, updatedValue)
        // Clear 'clipboard' value
        sessionStorage.removeItem('clipboard')

        const updatedEvent = { ...event }
        updatedEvent.target.value = updatedValue
        dataChange(updatedEvent)
      }
    }
  }

  const handleOnSelect = (value, fieldId) => {
    // Previously was an array of multiple values,
    // now this will always be a single value but we need to keep the array for legacy records
    const mockEvent = {
      target: {
        name: fieldId,
        value: value === null ? [] : [value]
      }
    }
    dataChange(mockEvent)
  }

  const openDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(true)
  }

  const closeDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(false)
  }

  const duplicate = event => {
    // Duplicate on state
    duplicateLineItem(index)
    // Duplicate on Parent
    onDuplicate(lineItem)
  }

  const duplicateMultiple = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        duplicate()
      }
    }
  }

  const handleDuplicate = HandleSingleAndDoubleClick(
    duplicate,
    openDuplicateItemMultipleModal
  )

  const handleRemove = event => {
    // if lineitem has id (which means it has been saved) just change the isDeleted flag
    // Otherwise remove the entire item
    if (lineItem.id) {
      // Create event and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: true
        }
      }
      dataChange(mockEvent)
    } else {
      // Remove from parent
      onRemove()
      // Remove from state
      deleteLineItem(index)
    }
  }

  const handleUndoRemove = event => {
    // Only shown to deleted products that have a real id
    if (lineItem.id) {
      // Create event and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: false
        }
      }
      dataChange(mockEvent)
    }
  }

  const handleWheelScrollInNumberField = event => {
    if (
      document.activeElement.type === 'number' &&
      document.activeElement.matches(':hover')
    ) {
      event.preventDefault()
    }
  }

  const shortcuts = useLineItemShortcuts(handleRemove, duplicate)

  useEffect(() => {
    // If line Item has been changed but not saved and the 'Add New Line Item' button is pressed
    // Stop entered values from being overwritten with original values
    if (item && !unsavedChanges) {
      // Populate from record
      useLineItemStore.setState({ [index]: { ...item } })
    }
  }, [index, item, unsavedChanges])

  useEffect(() => {
    document.addEventListener('wheel', handleWheelScrollInNumberField, {
      passive: false
    })
  }, [])

  const HasWeirdRebarLength = () => {
    const lengthUoM = lineItem.lengthUoM ? lineItem.lengthUoM : 'Millimeter'

    return (
      lineItem.name &&
      lineItem.name.toLowerCase().includes('rebar') &&
      lineItem.length * ConvertToMetersObject[lengthUoM] > 99.999
    )
  }

  const HasWeirdRebarWidth = () => {
    const widthUoM = lineItem.widthUoM ? lineItem.widthUoM : 'Millimeter'

    return (
      lineItem.name &&
      lineItem.name.toLowerCase().includes('rebar') &&
      lineItem.width * ConvertToMetersObject[widthUoM] > 0.999
    )
  }

  const handleVolumeCalculation = () => {
    const { calculatedVolume } = lineItem
    let volume
    if (!isImperial) {
      const {
        height,
        length,
        width,
        quantity,
        heightUoM,
        lengthUoM,
        widthUoM
      } = lineItem
      volume = calculateVolume({
        height,
        heightUoM,
        length,
        lengthUoM,
        width,
        widthUoM,
        quantity
      })
    } else if (isImperial) {
      const {
        heightYards,
        heightFeet,
        heightInches,
        lengthYards,
        lengthFeet,
        lengthInches,
        widthYards,
        widthFeet,
        widthInches,
        quantity
      } = lineItem
      volume = calculateImperialVolume({
        heightYards,
        heightFeet,
        heightInches,
        lengthYards,
        lengthFeet,
        lengthInches,
        widthYards,
        widthFeet,
        widthInches,
        quantity
      })
    }

    if (volume != null && volume != calculatedVolume) {
      useLineItemStore.setState({
        [index]: { ...lineItem, calculatedVolume: volume }
      })
    }

    handleWeightCalculation()
  }

  const handleWeightCalculation = event => {
    const {
      name,
      length,
      lengthUoM,
      width,
      widthUoM,
      quantity,
      estimatedWeight
    } = lineItem
    if (!name || !name.toLowerCase().includes('rebar')) {
      if (event) {
        handleFocus(event)
      }
    } else {
      const weight = estimateRebarWeight({
        diameter: width,
        diameterUoM: widthUoM,
        length,
        lengthUoM: lengthUoM,
        quantity
      })

      if (weight != null && weight != estimatedWeight) {
        useLineItemStore.setState({
          [index]: { ...lineItem, estimatedWeight: weight }
        })
      }
    }
  }

  const validateProducedAtDate = useCallback(() => {
    if (
      lineItem &&
      lineItem.productionTime &&
      deliveryArrivalDate &&
      new Date(lineItem.productionTime) > new Date(deliveryArrivalDate)
    ) {
      setProducedAtDateWarningMessage(
        PRODUCED_AT_DATE_AFTER_ARRIVAL_DATE_WARNING_MESSAGE
      )
      setShowProducedAtDateWarning(true)
    } else if (
      lineItem &&
      lineItem.productionTime &&
      deliveryExitDate &&
      new Date(lineItem.productionTime) > new Date(deliveryExitDate)
    ) {
      setProducedAtDateWarningMessage(
        PRODUCED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE
      )
      setShowProducedAtDateWarning(true)
    } else {
      setProducedAtDateWarningMessage('')
      setShowProducedAtDateWarning(false)
    }
    // Always return TRUE so we do not block form submission
    return true
  }, [deliveryArrivalDate, deliveryExitDate, lineItem])

  const validateUsedAtDate = useCallback(() => {
    if (
      lineItem &&
      lineItem.usedAtTime &&
      lineItem.productionTime &&
      new Date(lineItem.usedAtTime) < new Date(lineItem.productionTime)
    ) {
      setUsedAtDateWarningMessage(
        USED_AT_DATE_BEFORE_PRODUCED_AT_DATE_WARNING_MESSAGE
      )
      setShowUsedAtDateWarning(true)
    } else if (
      lineItem &&
      lineItem.usedAtTime &&
      deliveryExitDate &&
      new Date(lineItem.usedAtTime) > new Date(deliveryExitDate)
    ) {
      setUsedAtDateWarningMessage(USED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE)
      setShowUsedAtDateWarning(true)
    } else {
      setUsedAtDateWarningMessage('')
      setShowUsedAtDateWarning(false)
    }
    // Always return TRUE so we do not block form submission
    return true
  }, [deliveryExitDate, lineItem])

  useEffect(() => {
    if (lineItem) {
      validateUsedAtDate()
      validateProducedAtDate()
    }
  }, [lineItem, validateProducedAtDate, validateUsedAtDate])

  const usedAtTimePopulation = event => {
    // If OCR value has been clicked we trigger the main handleFocus function
    // Else if usedAtTime is empty copy value from production time
    const clipboardValue = sessionStorage.getItem('clipboard')
    if (clipboardValue && clipboardValue !== '') {
      handleFocus(event)
    } else if (event.target.value === '') {
      useLineItemStore.setState({
        [index]: { ...lineItem, usedAtTime: lineItem.productionTime }
      })
    }
  }

  const handleUsProductionTimeBlur = value => {
    const mockEvent = {
      target: {
        name: namePrefix + fields.lineItems.productionTime.path,
        value: usDateToFormDate(value)
      }
    }

    dataChange(mockEvent)
  }

  const handleUsUsedAtTimeBlur = value => {
    const mockEvent = {
      target: {
        name: namePrefix + fields.lineItems.usedAtTime.path,
        value: usDateToFormDate(value)
      }
    }

    dataChange(mockEvent)
  }

  const handleProductOpenChange = () => {
    setProductDetailsOpen(!productDetailsOpen)
    sessionStorage.setItem(`${recordId}-${index}-open`, !productDetailsOpen)
  }

  useEffect(() => {
    const open = sessionStorage.getItem(`${recordId}-${index}-open`)
    if (open) {
      setProductDetailsOpen(open === 'true')
    }
  }, [index, recordId])

  if (!lineItem) {
    return null
  }

  if (lineItem.isDeleted) {
    return (
      <FormProvider {...form}>
        <Section
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <Space
              direction="horizontal"
              align="center"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Product'}
                </span>
                <br />
                Deleted
              </Typography.Text>

              <Button
                type="text"
                tabIndex={-1}
                icon={<Undo />}
                onClick={handleUndoRemove}
                title="Undo delete"
              />
            </Space>
          )}
        </Section>
      </FormProvider>
    )
  }

  if (lineItem.isRemoved) {
    return (
      <FormProvider {...form}>
        <Section
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <Space
              direction="horizontal"
              align="center"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Product'}
                </span>
                <br />
                Removed by client
              </Typography.Text>
            </Space>
          )}
        </Section>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...form}>
      <Section
        className={'LineItem'}
        onKeyDown={shortcuts.onKeyDown}
        colour="alt"
      >
        {({ firstInputRef }) => (
          <details
            {...(productDetailsOpen ? { open: true } : {})}
            style={{ position: 'relative' }}
          >
            <LineItemTitle
              style={{ marginTop: '0' }}
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{ width: '272px' }}
                onClick={handleProductOpenChange}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                {name || 'New Product'}
              </Typography.Text>

              <ButtonGroup>
                <Button
                  type="text"
                  tabIndex={-1}
                  icon={<Icons.CopyOutlined />}
                  onClick={handleDuplicate}
                  title="Duplicate"
                />
                <Button
                  type="text"
                  tabIndex={-1}
                  danger
                  icon={<Icons.DeleteOutlined />}
                  onClick={handleRemove}
                  title="Delete"
                />
                {productDetailsOpen && (
                  <UpCircleFilledInline
                    title="Click to collapse"
                    onClick={handleProductOpenChange}
                  />
                )}
                {!productDetailsOpen && (
                  <DownCircleOutlinedInline
                    title="Click to expand"
                    onClick={handleProductOpenChange}
                  />
                )}
              </ButtonGroup>
            </LineItemTitle>
            <FormItem
              name={namePrefix + fields.lineItems.id.path}
              defaultValue={item.id ? item.id : lineItem.id}
              style={{ display: 'none' }}
            >
              <Input type="hidden" />
            </FormItem>
            <AutoCompleteFormItem
              label={fields.lineItems.name.label}
              name={namePrefix + fields.lineItems.name.path}
              alias={aliasPrefix + fields.lineItems.name.path}
              inputRef={firstInputRef}
              defaultValue={item.name ? item.name : lineItem.name}
              rules={Rules.MandatoryString}
              onBlur={dataChange}
              onChange={dataChange}
              onFocus={handleFocus}
              id={`deliveryLineItemName_${index}`}
              simpleDataValidationRules={CheckProductName}
              children={<TextArea autoSize />}
            />

            <FormSpace />

            <FormItem
              label={fields.lineItems.quantity.label}
              name={namePrefix + fields.lineItems.quantity.path}
              defaultValue={
                // Additional check because quantity defaults to 1
                item.quantity === 1 && lineItem.quantity
                  ? lineItem.quantity
                  : item.quantity
                    ? item.quantity
                    : lineItem.quantity
              }
              rules={Rules.NonNegativeNumber}
              onChange={dataChange}
              onFocus={handleFocus}
              onBlur={handleVolumeCalculation}
              id={`deliveryLineItemQuantity_${index}`}
              simpleDataValidationRules={CheckQuantity}
            >
              <Input type="number" min="0" step="any" />
            </FormItem>
            <FormSpace />
            <hr />
            <details
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
              {...(measurementsOpen ? { open: true } : {})}
              style={{ position: 'relative' }}
            >
              <summary
                onClick={() => {
                  setMeasurementsOpen(prev => !prev)
                }}
                style={{ width: 'fit-content' }}
              >
                <span>Measurements</span>
                {measurementsOpen && (
                  <UpCircleFilled title="Click to collapse" />
                )}
                {!measurementsOpen && (
                  <DownCircleOutlined title="Click to expand" />
                )}
              </summary>
              {!isImperial && (
                <FormRowWithValidation>
                  <div>
                    <FormItem
                      label={fields.lineItems.height.label}
                      name={namePrefix + fields.lineItems.height.path}
                      defaultValue={item.height ? item.height : lineItem.height}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      id={`deliveryLineItemHeight_${index}`}
                      simpleDataValidationRules={v =>
                        CheckHeightLengthWidth(v, 1.8, lineItem.heightUoM)
                      }
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        suffix={
                          <UnitSelector
                            name={namePrefix + fields.lineItems.heightUoM.path}
                            currentValue={
                              lineItem.heightUoM
                                ? lineItem.heightUoM
                                : 'Millimeter'
                            }
                            allValues={MeasurementUoM}
                            onChange={dataChange}
                          />
                        }
                      />
                    </FormItem>
                  </div>
                  <div
                    className={
                      HasWeirdRebarWidth() ? 'contains-validation-warning' : ''
                    }
                  >
                    <FormItem
                      label={fields.lineItems.width.label}
                      name={namePrefix + fields.lineItems.width.path}
                      defaultValue={item.width ? item.width : lineItem.width}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      id={`deliveryLineItemWidth_${index}`}
                      simpleDataValidationRules={v =>
                        CheckHeightLengthWidth(v, 1.8, lineItem.widthUoM)
                      }
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        suffix={
                          <UnitSelector
                            name={namePrefix + fields.lineItems.widthUoM.path}
                            currentValue={
                              lineItem.widthUoM
                                ? lineItem.widthUoM
                                : 'Millimeter'
                            }
                            allValues={MeasurementUoM}
                            onChange={dataChange}
                          />
                        }
                      />
                    </FormItem>
                  </div>
                  <div
                    className={
                      HasWeirdRebarLength() ? 'contains-validation-warning' : ''
                    }
                  >
                    <FormItem
                      label={fields.lineItems.length.label}
                      name={namePrefix + fields.lineItems.length.path}
                      defaultValue={item.length ? item.length : lineItem.length}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      id={`deliveryLineItemLength_${index}`}
                      simpleDataValidationRules={v =>
                        CheckHeightLengthWidth(v, 8.0, lineItem.lengthUoM)
                      }
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        suffix={
                          <UnitSelector
                            name={namePrefix + fields.lineItems.lengthUoM.path}
                            currentValue={
                              lineItem.lengthUoM ? lineItem.lengthUoM : 'Meter'
                            }
                            allValues={MeasurementUoM}
                            onChange={dataChange}
                          />
                        }
                      />
                    </FormItem>
                  </div>
                </FormRowWithValidation>
              )}

              {isImperial && (
                <>
                  <FormRowWithValidation>
                    <FormItem
                      label="Height"
                      name={
                        namePrefix + fieldsImperial.lineItems.heightYards.path
                      }
                      defaultValue={
                        item.heightYards
                          ? item.heightYards
                          : lineItem.heightYards
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.heightYards.label}
                        suffix="yd"
                      />
                    </FormItem>

                    <FormItem
                      label="Width"
                      name={
                        namePrefix + fieldsImperial.lineItems.widthYards.path
                      }
                      defaultValue={
                        item.widthYards ? item.widthYards : lineItem.widthYards
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.widthYards.label}
                        suffix="yd"
                      />
                    </FormItem>

                    <FormItem
                      label="Length"
                      name={
                        namePrefix + fieldsImperial.lineItems.lengthYards.path
                      }
                      defaultValue={
                        item.lengthYards
                          ? item.lengthYards
                          : lineItem.lengthYards
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.lengthYards.label}
                        suffix="yd"
                      />
                    </FormItem>
                  </FormRowWithValidation>

                  <FormRowWithValidation>
                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.heightFeet.path
                      }
                      defaultValue={
                        item.heightFeet ? item.heightFeet : lineItem.heightFeet
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.heightFeet.label}
                        suffix="ft"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.widthFeet.path
                      }
                      defaultValue={
                        item.widthFeet ? item.widthFeet : lineItem.widthFeet
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.widthFeet.label}
                        suffix="ft"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.lengthFeet.path
                      }
                      defaultValue={
                        item.lengthFeet ? item.lengthFeet : lineItem.lengthFeet
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.lengthFeet.label}
                        suffix="ft"
                      />
                    </FormItem>
                  </FormRowWithValidation>

                  <FormRowWithValidation>
                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.heightInches.path
                      }
                      defaultValue={
                        item.heightInches
                          ? item.heightInches
                          : lineItem.heightInches
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={
                          fieldsImperial.lineItems.heightInches.label
                        }
                        suffix="in"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.widthInches.path
                      }
                      defaultValue={
                        item.widthInches
                          ? item.widthInches
                          : lineItem.widthInches
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.widthInches.label}
                        suffix="in"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.lengthInches.path
                      }
                      defaultValue={
                        item.lengthInches
                          ? item.lengthInches
                          : lineItem.lengthInches
                      }
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={
                          fieldsImperial.lineItems.lengthInches.label
                        }
                        suffix="in"
                      />
                    </FormItem>
                  </FormRowWithValidation>
                </>
              )}

              {HasWeirdRebarWidth() && (
                <p className="validation-warning">
                  {REBAR_WIDTH_WARNING_MESSAGE}
                </p>
              )}
              {HasWeirdRebarLength() && (
                <p className="validation-warning">
                  {REBAR_LENGTH_WARNING_MESSAGE}
                </p>
              )}
            </details>
            <hr />
            <FormSpace />
            <FormRowWithValidation>
              <div
                style={{
                  display: 'flex',
                  marginBottom: '0',
                  flexDirection: 'column',
                  flex: '1 1 0'
                }}
              >
                <div
                  className="ant-space ant-space-horizontal ant-space-align-center"
                  style={{ gap: '8px' }}
                >
                  <div className="ant-space-item"></div>
                  <div className="ant-space-item">
                    <span
                      className="ant-typography"
                      style={{
                        flex: '1 1 0px',
                        overflow: 'hidden',
                        opacity: '1'
                      }}
                    >
                      {!isImperial
                        ? fields.lineItems.calculatedVolume.label
                        : fieldsImperial.lineItems.calculatedVolume.label}
                    </span>
                  </div>
                </div>
                <Input
                  type="number"
                  name={namePrefix + fields.lineItems.calculatedVolume.path}
                  value={
                    lineItem && lineItem.calculatedVolume
                      ? _.round(lineItem.calculatedVolume, isImperial ? 3 : 5)
                      : 0
                  }
                  onFocus={handleVolumeCalculation}
                  onChange={dataChange}
                  suffix={!isImperial ? 'm3' : 'cu yd'}
                  min="0"
                  step="any"
                  disabled
                />
              </div>

              <FormItem
                label={
                  !isImperial
                    ? fields.lineItems.givenVolume.label
                    : fieldsImperial.lineItems.givenVolume.label
                }
                name={namePrefix + fields.lineItems.givenVolume.path}
                defaultValue={
                  item.givenVolume ? item.givenVolume : lineItem.givenVolume
                }
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`deliveryLineItemVol_${index}`}
                simpleDataValidationRules={v =>
                  CheckVolume(v, lineItem.givenVolumeUoM)
                }
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={
                        namePrefix +
                        fieldsImperial.lineItems.givenVolumeUoM.path
                      }
                      currentValue={lineItem.givenVolumeUoM}
                      allValues={
                        !isImperial
                          ? METRIC_VOLUME_UNITS
                          : IMPERIAL_VOLUME_UNITS
                      }
                      onChange={dataChange}
                    />
                  }
                />
              </FormItem>
            </FormRowWithValidation>

            <FormRowWithValidation>
              <div
                style={{
                  display: 'flex',
                  marginBottom: '0',
                  flexDirection: 'column',
                  flex: '1 1 0'
                }}
              >
                <div
                  className="ant-space ant-space-horizontal ant-space-align-center"
                  style={{ gap: '8px' }}
                >
                  <div className="ant-space-item"></div>
                  <div className="ant-space-item">
                    <span
                      className="ant-typography"
                      style={{
                        flex: '1 1 0px',
                        overflow: 'hidden',
                        opacity: '1'
                      }}
                    >
                      {!isImperial
                        ? fields.lineItems.estimatedWeight.label
                        : fieldsImperial.lineItems.estimatedWeight.label}
                    </span>
                  </div>
                </div>
                <Input
                  type="number"
                  name={namePrefix + fields.lineItems.estimatedWeight.path}
                  value={
                    lineItem && lineItem.estimatedWeight
                      ? _.round(lineItem.estimatedWeight, isImperial ? 3 : 5)
                      : 0
                  }
                  rules={Rules.NonNegativeNumber}
                  onChange={dataChange}
                  onFocus={handleWeightCalculation}
                  suffix={!isImperial ? 't' : 'lbs'}
                  min="0"
                  step="any"
                  disabled
                />
              </div>

              <FormItem
                label={
                  !isImperial
                    ? fields.lineItems.weight.label
                    : fieldsImperial.lineItems.weight.label
                }
                name={namePrefix + fields.lineItems.weight.path}
                defaultValue={item.weight ? item.weight : lineItem.weight}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`deliveryLineItemWeight_${index}`}
                simpleDataValidationRules={v =>
                  CheckWeight(v, lineItem.weightUoM)
                }
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={
                        namePrefix + fieldsImperial.lineItems.weightUoM.path
                      }
                      currentValue={lineItem.weightUoM}
                      allValues={
                        !isImperial
                          ? METRIC_WEIGHT_UNITS
                          : IMPERIAL_WEIGHT_UNITS
                      }
                      onChange={dataChange}
                    />
                  }
                />
              </FormItem>
            </FormRowWithValidation>
            <FormSpace />
            <hr />
            <details
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
              {...(concreteDatesOpen ? { open: true } : {})}
              style={{ position: 'relative' }}
            >
              <summary
                onClick={() => {
                  setConcreteDatesOpen(prev => !prev)
                }}
                style={{ width: 'fit-content' }}
              >
                <span>Concrete Produced &amp; Used Dates</span>
                {concreteDatesOpen && (
                  <UpCircleFilled title="Click to collapse" />
                )}
                {!concreteDatesOpen && (
                  <DownCircleOutlined title="Click to expand" />
                )}
              </summary>
              <div
                className={
                  showProducedAtDateWarning ? 'contains-validation-warning' : ''
                }
              >
                <FormItem
                  label={fields.lineItems.productionTime.label}
                  name={namePrefix + fields.lineItems.productionTime.path}
                  defaultValue={
                    item.productionTime
                      ? item.productionTime
                      : lineItem.productionTime
                  }
                  onChange={dataChange}
                  onFocus={handleFocus}
                  id={`deliveryLineItemProducedAt_${index}`}
                >
                  <Input
                    type="datetime-local"
                    max="9999-12-31T23:59"
                    style={isImperial ? { display: 'none' } : {}}
                  />
                </FormItem>
                {isImperial && (
                  <>
                    <div
                      style={{
                        marginTop: '-8px',
                        fontSize: '12px',
                        color: '#888888'
                      }}
                    >
                      (MM/DD/YYYY, hh:mm AM/PM)
                    </div>
                    <DateTimeUS
                      className="DateTimeUS"
                      id={
                        namePrefix +
                        fieldsImperial.lineItems.productionTimeUS.path
                      }
                      relatedFieldPath={
                        namePrefix + fields.lineItems.productionTime.path
                      }
                      handleDateBlur={handleUsProductionTimeBlur}
                      value={usDateFormatter(
                        lineItem.productionTime ? lineItem.productionTime : ''
                      )}
                    />
                    <FormSpaceSmall />
                  </>
                )}
                {showProducedAtDateWarning && (
                  <p className="validation-warning">
                    {producedAtDateWarningMessage}
                  </p>
                )}
              </div>
              <div
                className={
                  showUsedAtDateWarning ? 'contains-validation-warning' : ''
                }
              >
                <div
                  className="ant-space ant-space-horizontal ant-space-align-center"
                  style={{ gap: '8px' }}
                >
                  <div className="ant-space-item"></div>
                  <div className="ant-space-item">
                    <label
                      htmlFor={`deliveryLineItemUsedAt_${index}`}
                      className="ant-typography"
                      style={{
                        flex: '1 1 0px',
                        overflow: 'hidden',
                        opacity: '1',
                        color: 'inherit',
                        fontSize: 'inherit'
                      }}
                    >
                      {fields.lineItems.usedAtTime.label}
                    </label>
                  </div>
                </div>
                <Input
                  type="datetime-local"
                  name={namePrefix + fields.lineItems.usedAtTime.path}
                  value={lineItem && lineItem.usedAtTime}
                  max="9999-12-31T23:59"
                  onChange={dataChange}
                  onFocus={usedAtTimePopulation}
                  id={`deliveryLineItemUsedAt_${index}`}
                  style={isImperial ? { display: 'none' } : {}}
                />
                {isImperial && (
                  <>
                    <div
                      style={{
                        marginTop: '-4px',
                        fontSize: '12px',
                        color: '#888888'
                      }}
                    >
                      (MM/DD/YYYY, hh:mm AM/PM)
                    </div>
                    <DateTimeUS
                      className="DateTimeUS"
                      id={
                        namePrefix + fieldsImperial.lineItems.usedAtTimeUS.path
                      }
                      relatedFieldPath={
                        namePrefix + fields.lineItems.usedAtTime.path
                      }
                      handleDateBlur={handleUsUsedAtTimeBlur}
                      value={usDateFormatter(
                        lineItem.usedAtTime ? lineItem.usedAtTime : ''
                      )}
                    />
                  </>
                )}
                {showUsedAtDateWarning && (
                  <>
                    <FormSpaceSmall />
                    <p className="validation-warning">
                      {usedAtDateWarningMessage}
                    </p>
                  </>
                )}
              </div>
            </details>
            <hr />
            <FormSpace />
            <FormItem
              label={fields.lineItems.givenCertifications.label}
              name={namePrefix + fields.lineItems.givenCertifications.path}
              defaultValue={
                item.givenCertifications && item.givenCertifications.length > 0
                  ? item.givenCertifications
                  : lineItem.givenCertifications
              }
              onChange={dataChange}
              onFocus={handleFocus}
            >
              <Select
                allowClear
                placeholder="Select certifications"
                style={{ width: '100%' }}
                onClear={() =>
                  handleOnSelect(
                    null,
                    namePrefix + fields.lineItems.givenCertifications.path
                  )
                }
                onSelect={value =>
                  handleOnSelect(
                    value,
                    namePrefix + fields.lineItems.givenCertifications.path
                  )
                }
                options={DeliveryProductCertOptions.map(item => ({
                  value: item,
                  label: item
                }))}
              />
            </FormItem>

            <ReferenceProductItemSelect
              namePrefix={namePrefix}
              lineItem={item}
              onChange={dataChange}
            />
          </details>
        )}
      </Section>
      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isDuplicateItemsMultipleOpen}
        onClose={closeDuplicateItemMultipleModal}
        onSubmit={val => {
          duplicateMultiple(val)
        }}
        duplicateLineItemName={item.name}
      />
    </FormProvider>
  )
}

export const UpCircleFilled = styled(Icons.UpCircleFilled)`
  font-size: 20px;
  color: ${colours.TEAL};
  position: absolute;
  top: 1px;
  right: 0;
`
export const DownCircleOutlined = styled(Icons.DownCircleOutlined)`
  font-size: 20px;
  color: ${colours.TEAL};
  position: absolute;
  top: 1px;
  right: 0;
`
export const UpCircleFilledInline = styled(Icons.UpCircleFilled)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
export const DownCircleOutlinedInline = styled(Icons.DownCircleOutlined)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
`
